import { Alert, UserInfo } from '../types';
import { AlertService } from './AlertService';

export class AlertSystem {
  public alerts: Alert[];
  public userInfo: UserInfo | null;
  private dismissedAlerts: string[];
  private isLoading: boolean;

  constructor(alerts: Alert[] = [], userInfo: UserInfo | null = null) {
    this.alerts = alerts;
    this.userInfo = userInfo;
    this.dismissedAlerts = this.loadDismissedAlerts();
    this.isLoading = false;
  }

  static async initialize(userInfo: UserInfo | null = null): Promise<AlertSystem> {
    const alerts = await AlertService.fetchAlerts();
    return new AlertSystem(alerts, userInfo);
  }

  async reload(): Promise<void> {
    this.isLoading = true;
    try {
      this.alerts = await AlertService.refreshAlerts();
    } finally {
      this.isLoading = false;
    }
  }

  private loadDismissedAlerts(): string[] {
    try {
      const dismissed = localStorage.getItem('dismissedAlerts');
      return dismissed ? JSON.parse(dismissed) : [];
    } catch (error) {
      console.error('Error loading dismissed alerts:', error);
      return [];
    }
  }

  private saveDismissedAlerts(): void {
    try {
      localStorage.setItem('dismissedAlerts', JSON.stringify(this.dismissedAlerts));
    } catch (error) {
      console.error('Error saving dismissed alerts:', error);
    }
  }

  public dismissAlert(alertId: string): void {
    if (!this.dismissedAlerts.includes(alertId)) {
      this.dismissedAlerts.push(alertId);
      this.saveDismissedAlerts();
    }
  }

  public shouldShowAlert(alert: Alert): boolean {
    if (this.dismissedAlerts.includes(alert.id)) {
      return false;
    }

    if (alert.target === 'all') {
      if (alert.excludeRoles && this.userInfo?.isLogged) {
        return !alert.excludeRoles.includes(this.userInfo.role || '');
      }
      return true;
    }

    if (alert.target === 'logged') {
      if (!this.userInfo?.isLogged) return false;
      if (alert.excludeRoles) {
        return !alert.excludeRoles.includes(this.userInfo.role || '');
      }
      return true;
    }

    if (alert.target === 'not_logged') {
      return !this.userInfo?.isLogged;
    }

    if (Array.isArray(alert.target)) {
      return this.userInfo?.isLogged === true && alert.target.includes(this.userInfo.role || '');
    }

    return false;
  }

  public getAlertsToShow(): Alert[] {
    return this.alerts.filter(alert => this.shouldShowAlert(alert)).sort((a, b) => a.priority - b.priority);
  }

  public getLoadingState(): boolean {
    return this.isLoading;
  }
}
